/**
 * Turning "what shape, how many chairs" into something the API will accept.
 *
 * The database decides capacity from whichever count set the shape makes
 * authoritative and zeroes the other set with a CHECK, so a round table cannot
 * carry a stale `seats_top` from an earlier edit. Studio mirrors that here
 * rather than posting whatever is in the form: a host who switches a rectangle
 * to a round should get a message, not a 23514.
 */
import type {
  CreateSeatingTableRequest,
  SeatingTable,
  SeatingTableShape,
  UpdateSeatingTableRequest,
} from '../types/seating';

/** Bounds the seat_index domain; the API rejects anything outside it. */
export const MAX_TABLE_CAPACITY = 60;

export interface TableDraft {
  label: string;
  shape: SeatingTableShape;
  /** Round only. */
  seatCount: number;
  /** Rectangle and square only. */
  seatsTop: number;
  seatsBottom: number;
  seatsLeft: number;
  seatsRight: number;
  /** How many of this table to add in one go. */
  quantity: number;
}

export const EMPTY_TABLE_DRAFT: TableDraft = {
  label: '',
  shape: 'round',
  seatCount: 10,
  seatsTop: 11,
  seatsBottom: 11,
  seatsLeft: 1,
  seatsRight: 1,
  quantity: 1,
};

/**
 * The tables a host actually rents.
 *
 * The long one is first among the rectangles on purpose: "a really long table"
 * is the case the operator named, and expressing it as 11/11/1/1 — two long
 * sides and a chair at each head — is exactly the thing that is not obvious
 * from four numbered boxes.
 */
export const TABLE_PRESETS: ReadonlyArray<{
  id: string;
  label: string;
  hint: string;
  draft: Pick<TableDraft, 'shape' | 'seatCount' | 'seatsTop' | 'seatsBottom' | 'seatsLeft' | 'seatsRight'>;
}> = [
  {
    id: 'round-10',
    label: 'Round of 10',
    hint: 'The banquet standard — a 1.9m round.',
    draft: { shape: 'round', seatCount: 10, seatsTop: 0, seatsBottom: 0, seatsLeft: 0, seatsRight: 0 },
  },
  {
    id: 'round-8',
    label: 'Round of 8',
    hint: 'A smaller round for a tighter room.',
    draft: { shape: 'round', seatCount: 8, seatsTop: 0, seatsBottom: 0, seatsLeft: 0, seatsRight: 0 },
  },
  {
    id: 'long-24',
    label: 'Long table, 24',
    hint: '11 a side and a chair at each head — 7.2m of table.',
    draft: { shape: 'rectangle', seatCount: 0, seatsTop: 11, seatsBottom: 11, seatsLeft: 1, seatsRight: 1 },
  },
  {
    id: 'long-12',
    label: 'Long table, 12',
    hint: '5 a side, a chair at each head.',
    draft: { shape: 'rectangle', seatCount: 0, seatsTop: 5, seatsBottom: 5, seatsLeft: 1, seatsRight: 1 },
  },
  {
    id: 'square-8',
    label: 'Square of 8',
    hint: 'Two chairs a side.',
    draft: { shape: 'square', seatCount: 0, seatsTop: 2, seatsBottom: 2, seatsLeft: 2, seatsRight: 2 },
  },
  {
    id: 'head-10',
    label: 'Head table, 10',
    hint: 'Eight facing the room, one at each end, nobody with their back to it.',
    draft: { shape: 'rectangle', seatCount: 0, seatsTop: 8, seatsBottom: 0, seatsLeft: 1, seatsRight: 1 },
  },
];

/**
 * What a host meant by what they typed into a seat-count box.
 *
 * The boxes are `type="text"` because a number input cannot be corrected once
 * it is wrong: React only rewrites one when `node.valueAsNumber` differs from
 * the value being rendered, and "020" already reads back as 20 — so the state
 * said 20, the box said 020, and nothing could make them agree. Every box also
 * starts at a digit, so a typed number lands in front of one; dropping the
 * leading zero is what makes it look like it was typed.
 *
 * Three digits is a ceiling on the DOM, not on taste. `validateTableDraft`
 * still refuses anything over {@link MAX_TABLE_CAPACITY}, with a sentence.
 */
export function parseSeatInput(raw: string): number {
  const digits = raw.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, 3);
  return digits ? Number.parseInt(digits, 10) : 0;
}

export function draftCapacity(draft: TableDraft): number {
  if (draft.shape === 'round') return Math.max(Math.floor(draft.seatCount) || 0, 0);
  return [draft.seatsTop, draft.seatsBottom, draft.seatsLeft, draft.seatsRight].reduce(
    (sum, side) => sum + Math.max(Math.floor(side) || 0, 0),
    0,
  );
}

/**
 * Keep a square square. The database refuses asymmetric opposite sides on a
 * square (it renders 1:1), so editing one side has to move its opposite — the
 * alternative is a host typing four numbers and being told at save time that
 * their square is a rectangle.
 */
export function applySquareSymmetry(draft: TableDraft): TableDraft {
  if (draft.shape !== 'square') return draft;
  return { ...draft, seatsBottom: draft.seatsTop, seatsRight: draft.seatsLeft };
}

/** '' when the draft will be accepted. Mirrors the CHECKs, in host language. */
export function validateTableDraft(draft: TableDraft): string {
  if (!draft.label.trim()) return 'Give the table a name — “Table 1”, “Head table”. Staff read it off the chart.';
  const capacity = draftCapacity(draft);
  if (capacity < 1) return 'A table needs at least one chair.';
  if (capacity > MAX_TABLE_CAPACITY) {
    return `A table can seat at most ${MAX_TABLE_CAPACITY}. Split it into two and put them side by side.`;
  }
  if (draft.shape === 'square' && (draft.seatsTop !== draft.seatsBottom || draft.seatsLeft !== draft.seatsRight)) {
    return 'A square renders 1:1, so opposite sides must match. Use a rectangle for anything else.';
  }
  if (draft.quantity < 1 || draft.quantity > 40) return 'Add between 1 and 40 tables at a time.';
  return '';
}

/**
 * Labels for a batch, skipping anything already in the room.
 *
 * "Table 4" twice is a unique-index violation and, worse, a service error: two
 * tables with one name is how a course lands in front of the wrong ten people.
 * Numbering resumes past the collision rather than failing the whole batch.
 */
export function nextTableLabels(prefix: string, quantity: number, existing: string[]): string[] {
  const trimmed = prefix.trim();
  const taken = new Set(existing.map((label) => label.trim().toLowerCase()));
  const wanted = Math.max(Math.floor(quantity) || 0, 0);
  if (wanted <= 1) {
    // A single table keeps the name the host typed; only a batch gets numbered.
    return trimmed && !taken.has(trimmed.toLowerCase()) ? [trimmed] : uniqueNumbered(trimmed, 1, taken);
  }
  return uniqueNumbered(trimmed, wanted, taken);
}

function uniqueNumbered(prefix: string, quantity: number, taken: Set<string>): string[] {
  const labels: string[] = [];
  let n = 1;
  while (labels.length < quantity) {
    const label = `${prefix} ${n}`.trim();
    n += 1;
    if (taken.has(label.toLowerCase())) continue;
    taken.add(label.toLowerCase());
    labels.push(label);
  }
  return labels;
}

/**
 * The bodies to POST. The inauthoritative count set is zeroed here, not left to
 * the API to interpret: shape decides which numbers mean anything, and sending
 * both is how a round table ends up claiming eleven chairs a side.
 */
export function tableRequests(draft: TableDraft, existingLabels: string[]): CreateSeatingTableRequest[] {
  const normalized = applySquareSymmetry(draft);
  const counts =
    normalized.shape === 'round'
      ? { seatCount: normalized.seatCount, seatsTop: 0, seatsBottom: 0, seatsLeft: 0, seatsRight: 0 }
      : {
          seatCount: 0,
          seatsTop: normalized.seatsTop,
          seatsBottom: normalized.seatsBottom,
          seatsLeft: normalized.seatsLeft,
          seatsRight: normalized.seatsRight,
        };

  return nextTableLabels(normalized.label, normalized.quantity, existingLabels).map((label, index) => ({
    label,
    shape: normalized.shape,
    ...counts,
    displayOrder: existingLabels.length + index,
  }));
}

// ---- editing a table that already exists ----

/**
 * The form, filled from the table in the room.
 *
 * `quantity` is meaningless here and is pinned at 1 so the shared validator and
 * the shared preview both work unchanged — an edit is one table by definition.
 */
export function draftFromTable(table: SeatingTable): TableDraft {
  const shape: SeatingTableShape =
    table.shape === 'rectangle' || table.shape === 'square' ? table.shape : 'round';
  return {
    label: table.label ?? '',
    shape,
    seatCount: table.seatCount || 0,
    seatsTop: table.seatsTop || 0,
    seatsBottom: table.seatsBottom || 0,
    seatsLeft: table.seatsLeft || 0,
    seatsRight: table.seatsRight || 0,
    quantity: 1,
  };
}

/**
 * The PATCH body. Same zeroing rule as {@link tableRequests}: shape decides
 * which count set is authoritative and the other is sent as zeros, because the
 * database CHECK refuses a round table carrying a stale `seats_top` from before
 * the host changed its shape — and PatchValue means an omitted field keeps the
 * stale value rather than clearing it. This is the one place where "omit to
 * preserve" is the wrong instinct.
 */
export function tableEditRequest(draft: TableDraft, displayOrder?: number): UpdateSeatingTableRequest {
  const normalized = applySquareSymmetry(draft);
  const counts =
    normalized.shape === 'round'
      ? { seatCount: normalized.seatCount, seatsTop: 0, seatsBottom: 0, seatsLeft: 0, seatsRight: 0 }
      : {
          seatCount: 0,
          seatsTop: normalized.seatsTop,
          seatsBottom: normalized.seatsBottom,
          seatsLeft: normalized.seatsLeft,
          seatsRight: normalized.seatsRight,
        };
  return {
    label: normalized.label.trim(),
    shape: normalized.shape,
    ...counts,
    ...(displayOrder === undefined ? {} : { displayOrder }),
  };
}

/**
 * Same constraints, minus the batch-only ones.
 *
 * An edit has no quantity, and its own name is not a collision with itself —
 * `existingLabels` is the room without this table in it, which is what makes
 * "keep the name, change the chairs" a legal save rather than a duplicate_label
 * refusal the host cannot explain.
 */
export function validateTableEdit(draft: TableDraft, otherLabels: string[]): string {
  const problem = validateTableDraft({ ...draft, quantity: 1 });
  if (problem) return problem;
  const taken = new Set(otherLabels.map((label) => label.trim().toLowerCase()));
  if (taken.has(draft.label.trim().toLowerCase())) {
    return 'Another table in this room already has that name. Two "Table 4"s is a service error waiting to happen.';
  }
  return '';
}

/**
 * How many occupants a shrink would strand, from the table as it stands.
 *
 * The API is the authority and refuses with the actual list; this is what lets
 * the dialog say so BEFORE the host presses Save, while the number of chairs is
 * still theirs to change.
 */
export function occupantsBeyond(table: SeatingTable, capacity: number): number {
  return (table.seats ?? []).filter(
    (seat) => seat.assignment && seat.seatIndex >= capacity,
  ).length;
}
